import { Container, Sprite } from "pixi.js";
import {
  CORE_PEAK_ALPHA,
  GLOW_PEAK_ALPHA,
  haloCoreTexture,
  haloGlowTexture,
  haloScale,
  haloStreakTexture,
  streakScale,
} from "./halo-texture";
import type { ActivePress, NoteEffect, NoteEffectFrame } from "./types";

const HALO_BASE_RADIUS = 12;

/** Outermost ring of the stack this replaces, and innermost white one. */
const GLOW_RADIUS_FACTOR = 2.4;
const CORE_RADIUS_FACTOR = 0.65;

/**
 * Width over height. The halo is not a ball resting on the key, it is light
 * coming off a note as it crosses the playhead, so it spreads along the line
 * rather than in a circle around it — the same direction as the playhead's
 * own glow, which it now sits inside instead of on top of.
 *
 * Applied as `√aspect` wide and `1/√aspect` tall, so flattening the halo does
 * not also change how much of the roll it covers. The core is rounder than
 * the glow: it is the point of impact, and a strongly oval one reads as a
 * smear.
 */
const GLOW_ASPECT = 1.8;
const CORE_ASPECT = 1.35;

/** Half-length of the horizontal streak, in glow radii. */
const STREAK_REACH = 1.8;
const STREAK_PEAK_ALPHA = 0.5;

/**
 * The diagonal arms, as a fraction of the horizontal one, and the velocity
 * they start appearing at.
 *
 * Gated on velocity on purpose. The reference for this look is a single
 * source on a black field; the roll can hold five keys at once, each with its
 * own colour, and a full star on every one of them is a Christmas tree. So
 * the horizontal streak is always there, and the arms are what a hard attack
 * earns — which also gives velocity something to say that size and brightness
 * were not already saying.
 */
const SPIKE_REACH = 0.55;
const SPIKE_PEAK_ALPHA = 0.35;
const SPIKE_VELOCITY_FLOOR = 0.55;

const SPIKE_ANGLES = [Math.PI / 4, -Math.PI / 4] as const;

type HaloState = {
  glow: Sprite;
  streak: Sprite;
  spikes: readonly Sprite[];
  core: Sprite;
  active: boolean;
};

/**
 * Static halo at the playhead line for each strike crossing it. Size and
 * brightness scale with note-on velocity.
 *
 * Additive sprites over two baked textures, a radial gradient and a streak:
 * see `halo-texture.ts` for why it is not a blurred stack of circles.
 */
export class HaloEffect implements NoteEffect {
  protected container: Container;
  private byMidi = new Map<number, HaloState>();
  private pool: HaloState[] = [];

  constructor(parent: Container) {
    this.container = new Container();
    parent.addChild(this.container);
  }

  update(frame: NoteEffectFrame): void {
    for (const state of this.byMidi.values()) {
      state.active = false;
    }

    for (const press of frame.presses) {
      let state = this.byMidi.get(press.midi);

      if (!state) {
        state = this.acquire();
        this.byMidi.set(press.midi, state);
      }

      state.active = true;
      drawHalo(state, press, HALO_BASE_RADIUS);
    }

    for (const [midi, state] of this.byMidi) {
      if (!state.active) {
        hideHalo(state);
        this.byMidi.delete(midi);
        this.pool.push(state);
      }
    }
  }

  isAnimating(): boolean {
    return this.byMidi.size > 0;
  }

  destroy(): void {
    for (const state of this.byMidi.values()) {
      disposeHalo(state);
    }

    for (const state of this.pool) {
      disposeHalo(state);
    }

    this.byMidi.clear();
    this.pool.length = 0;
    this.container.destroy({ children: true });
  }

  private acquire(): HaloState {
    const reused = this.pool.pop();

    if (reused) {
      return reused;
    }

    return createHalo(this.container);
  }
}

/**
 * Every sprite one halo owns, in no particular order: they all add.
 *
 * Mémorisé par halo : `drawHalo` tourne pour chaque touche tenue à chaque
 * image, et le tableau ne change jamais après la création.
 */
const spritesByHalo = new WeakMap<HaloState, readonly Sprite[]>();

function spritesOf(state: HaloState): readonly Sprite[] {
  let sprites = spritesByHalo.get(state);

  if (sprites === undefined) {
    sprites = [state.glow, state.streak, ...state.spikes, state.core];
    spritesByHalo.set(state, sprites);
  }

  return sprites;
}

/**
 * One halo: the coloured glow, the streak and its arms, the white core.
 *
 * All additive, and nothing above them is filtered, so they add to the roll
 * rather than to an offscreen copy of themselves. Additive compositing is
 * order-independent, so the order they are added in decides nothing.
 */
export function createHalo(parent: Container): HaloState {
  const state: HaloState = {
    glow: new Sprite(haloGlowTexture()),
    streak: new Sprite(haloStreakTexture()),
    spikes: SPIKE_ANGLES.map(() => new Sprite(haloStreakTexture())),
    core: new Sprite(haloCoreTexture()),
    active: false,
  };

  state.spikes.forEach((spike, i) => {
    spike.rotation = SPIKE_ANGLES[i];
  });

  for (const sprite of spritesOf(state)) {
    sprite.anchor.set(0.5);
    sprite.blendMode = "add";
    parent.addChild(sprite);
  }

  return state;
}

export function hideHalo(state: HaloState): void {
  for (const sprite of spritesOf(state)) {
    sprite.visible = false;
  }
}

export function disposeHalo(state: HaloState): void {
  for (const sprite of spritesOf(state)) {
    sprite.destroy();
  }
}

/**
 * Radius and alpha both scale with note-on velocity:
 *   radius : 50% (pp) → 120% (ff) of the base radius
 *   alpha  : 35% (pp) → 100% (ff)
 */
export function drawHalo(
  state: HaloState,
  press: ActivePress,
  baseRadius: number,
): void {
  const v = Math.max(0, Math.min(1, press.velocity));
  const r = baseRadius * (0.5 + 0.7 * v);
  const a = 0.35 + 0.65 * v;

  for (const sprite of spritesOf(state)) {
    sprite.position.set(press.x, press.y);
    sprite.visible = true;
  }

  state.glow.tint = press.color;
  state.glow.alpha = Math.min(1, GLOW_PEAK_ALPHA * a);
  spread(state.glow, r * GLOW_RADIUS_FACTOR, GLOW_ASPECT);

  state.core.alpha = Math.min(1, CORE_PEAK_ALPHA * a);
  spread(state.core, r * CORE_RADIUS_FACTOR, CORE_ASPECT);

  const reach = r * GLOW_RADIUS_FACTOR * STREAK_REACH;

  state.streak.tint = press.color;
  state.streak.alpha = Math.min(1, STREAK_PEAK_ALPHA * a);
  state.streak.scale.set(streakScale(reach));

  // Nothing below the floor, then the full arm at `ff`. Hidden rather than
  // drawn at zero: a sprite nobody can see is still a quad to batch, and a
  // held key redraws every frame.
  const spikeAlpha =
    v <= SPIKE_VELOCITY_FLOOR
      ? 0
      : ((v - SPIKE_VELOCITY_FLOOR) / (1 - SPIKE_VELOCITY_FLOOR)) *
        SPIKE_PEAK_ALPHA;

  for (const spike of state.spikes) {
    spike.visible = spikeAlpha > 0;
    spike.tint = press.color;
    spike.alpha = spikeAlpha;
    spike.scale.set(streakScale(reach * SPIKE_REACH));
  }
}

/** Scales a sprite to `radius`, flattened to `aspect` at constant area. */
function spread(sprite: Sprite, radius: number, aspect: number): void {
  const stretch = Math.sqrt(aspect);

  sprite.scale.set(
    haloScale(radius * stretch),
    haloScale(radius / stretch),
  );
}

export type { HaloState };
